import React, { useState, useEffect, useRef } from 'react';
import Cta from '@components/CTA';
import MobileDropdown from '@components/MobileSectionNavigation/index';
import useMobileDetection from "@klickinc/kfs-components-use-mobile-detection";
import { glassStyleSectionNavigation } from '../../styles/common.css';

/**
 * SectionNavigation component for navigating between sections on a page
 * 
 * @param {Object} props - Component props
 * @param {Array} props.sections - Array of section objects with id and title
 * @param {string} props.activeSection - ID of the currently active section
 * @param {string} props.style - Additional CSS classes for styling
 * @param {Function} props.onSectionChange - Callback when section changes
 * @returns {React.ReactElement} SectionNavigation component
 */
const SectionNavigation = ({ 
	sections = [], 
	activeSection = '', 
	style = '', 
	onSectionChange = () => {} 
}) => {
	const [currentSection, setCurrentSection] = useState(activeSection);
	const navRef = useRef(null);
	const isMobile = useMobileDetection();
	const [isHeaderVisible, setIsHeaderVisible] = useState(false);
	const [isFixed, setIsFixed] = useState(false);
	const [initialPosition, setInitialPosition] = useState(null);
	const [hasInitialScroll, setHasInitialScroll] = useState(false);

	// Handle initial load scroll
	useEffect(() => {
		if (!hasInitialScroll && sections.length > 0) {
			const hash = window.location.hash;
			if (hash) {
				const sectionId = hash.substring(1);
				const sectionExists = sections.some(section => section.contentfulid === sectionId);
				if (sectionExists) {
					// Wait for the page to be fully loaded and rendered
					setTimeout(() => {
						const sectionElement = document.getElementById(sectionId);
						if (sectionElement) {
							setCurrentSection(sectionId);
							onSectionChange(sectionId);
							// Use the same smoothScrollTo function with a slightly longer duration for initial load
							smoothScrollTo(sectionElement, 1200);
						}
					}, 100); // Small delay to ensure DOM is ready
				}
			}
			setHasInitialScroll(true);
		}
	}, [hasInitialScroll, onSectionChange, sections]);

	// Update current section when activeSection prop changes
	useEffect(() => {
		if (activeSection && activeSection !== currentSection) {
			setCurrentSection(activeSection);
		}
	}, [activeSection, currentSection]);

	// Set up intersection observer to detect when sections are in view
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setCurrentSection(entry.target.id);
						onSectionChange(entry.target.id);
					}
				});
			},
			{
				rootMargin: '-50% 0px -50% 0px', // Trigger when section is in middle of viewport
				threshold: 0
			}
		);

		// Observe all sections
		sections.forEach((section) => {
			const element = document.getElementById(section.contentfulid);
			if (element) {
				observer.observe(element);
			}
		});

		return () => {
			observer.disconnect();
		};
	}, [sections, onSectionChange]);

	// Custom smooth scrolling function with industry-standard smoothness
	const smoothScrollTo = (element, duration = 1000) => {
		if (!element) return;
		
		// Get element position accounting for any header offset
		const isMobile = window.innerWidth < 1024;
		const headerHeight = isMobile ? 160 : 120; // Adjust header height based on mobile/desktop
		const targetPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight;
		const startPosition = window.scrollY;
		const totalDistance = targetPosition - startPosition;
		
		// Cancel any running animations to prevent conflicts
		if (window.scrollAnimation) {
			window.cancelAnimationFrame(window.scrollAnimation);
		}
		
		// Optimize duration based on distance for natural feel
		// Industry standard: scroll speed of ~2000px per second for large distances
		// with a base minimum time to ensure smoothness for short distances
		const baseDuration = 800; // minimum animation time (ms)
		const distanceFactor = Math.abs(totalDistance) / 2000; // time per 2000px
		const calculatedDuration = Math.max(baseDuration, distanceFactor * 1000);
		const actualDuration = Math.min(calculatedDuration, 2000); // cap at 2 seconds
		
		let startTime = null;
		
		// Bezier curve parameters - mimics CSS ease-in-out but with more control
		// Using Apple's standard animation curve (similar to iOS/macOS)
		const p1 = { x: 0.25, y: 0.1 }; // control point 1
		const p2 = { x: 0.25, y: 1.0 }; // control point 2
		
		// Cubic Bezier function implementation
		const cubicBezier = (t, p1x, p1y, p2x, p2y) => {
			// Calculate the coefficients
			const cx = 3 * p1x;
			const bx = 3 * (p2x - p1x) - cx;
			const ax = 1 - cx - bx;
			
			const cy = 3 * p1y;
			const by = 3 * (p2y - p1y) - cy;
			const ay = 1 - cy - by;
			
			// Use Newton-Raphson iteration to solve for t given x
			const solveForT = (x, epsilon = 0.0001) => {
				let t = x; // Initial guess
				
				// Newton's method
				for (let i = 0; i < 8; i++) {
					const xGuess = ((ax * t + bx) * t + cx) * t;
					const diff = xGuess - x;
					if (Math.abs(diff) < epsilon) break;
					
					const derivative = (3 * ax * t + 2 * bx) * t + cx;
					if (Math.abs(derivative) < epsilon) break;
					
					t = t - diff / derivative;
				}
				
				return t;
			};
			
			// Given t, find the corresponding y value
			const t2 = solveForT(t);
			return ((ay * t2 + by) * t2 + cy) * t2;
		};
		
		const animation = (currentTime) => {
			if (startTime === null) startTime = currentTime;
			const timeElapsed = currentTime - startTime;
			
			// Calculate progress (0 to 1)
			const progress = Math.min(timeElapsed / actualDuration, 1);
			
			// Apply custom Apple-like easing curve
			const easedProgress = cubicBezier(progress, p1.x, p1.y, p2.x, p2.y);
			
			// Calculate new position
			const newPosition = startPosition + totalDistance * easedProgress;
			
			// Apply the scroll
			window.scrollTo(0, newPosition);
			
			// Continue animation if not complete
			if (progress < 1) {
				window.scrollAnimation = requestAnimationFrame(animation);
			}
		};
		
		window.scrollAnimation = requestAnimationFrame(animation);
	};

	// Handle section click with debouncing to prevent multiple rapid scrolls
	const handleSectionClick = (sectionId) => {
		setCurrentSection(sectionId);
		onSectionChange(sectionId);
		
		// Clear any pending scroll timeout
		if (window.scrollTimeout) {
			clearTimeout(window.scrollTimeout);
		}
		
		// Slight delay to debounce multiple rapid clicks
		window.scrollTimeout = setTimeout(() => {
			const sectionElement = document.getElementById(sectionId);
			if (sectionElement) {
				smoothScrollTo(sectionElement);
			}
		}, 50);
	};

	// Use useEffect to handle the header visibility check
	useEffect(() => {
		let lastScrollY = window.scrollY;
		let frameId = null;

		const updateHeaderVisibility = () => {
			const currentScrollY = window.scrollY;
			const isScrollingDown = currentScrollY > lastScrollY;
			
			if (currentScrollY > 160) {
				setIsHeaderVisible(isScrollingDown);
			} else {
				setIsHeaderVisible(false);
			}
			
			lastScrollY = currentScrollY;
			frameId = null;
		};

		const onScroll = () => {
			if (frameId) {
				return; // Skip if we're already planning to update
			}
			
			frameId = requestAnimationFrame(updateHeaderVisibility);
		};

		// Initial check
		updateHeaderVisibility();
		
		window.addEventListener('scroll', onScroll, { passive: true });
		
		return () => {
			if (frameId) {
				cancelAnimationFrame(frameId);
			}
			window.removeEventListener('scroll', onScroll);
		};
	}, []);

	// Add the new useEffect for fixed positioning
	useEffect(() => {
		const handleScroll = () => {
			if (!initialPosition) {
				setInitialPosition(navRef.current?.getBoundingClientRect().top + window.scrollY);
				return;
			}

			const scrollPosition = window.scrollY;
			setIsFixed(scrollPosition > initialPosition - 20);
		};

		// Set initial position on mount
		if (navRef.current) {
			setInitialPosition(navRef.current.getBoundingClientRect().top + window.scrollY);
		}

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [initialPosition]);

	// Handle URL hash changes
	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash;
			if (hash) {
				const sectionId = hash.substring(1);
				const sectionExists = sections.some(section => section.contentfulid === sectionId);
				if (sectionExists) {
					const sectionElement = document.getElementById(sectionId);
					if (sectionElement) {
						setCurrentSection(sectionId);
						onSectionChange(sectionId);
						smoothScrollTo(sectionElement);
					}
				}
			}
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => window.removeEventListener('hashchange', handleHashChange);
	}, [sections, onSectionChange]);

	if (isMobile) {
		return (
			<MobileDropdown 
				options={sections.map(section => ({
					...section,
					contentfulid: section.contentfulid,
					label: section.label
				}))}
				activeOption={currentSection}
				placeholder="Navigate to section"
				style={`-mb-6 z-30 ${isHeaderVisible ? 'top-0' : 'top-40'}`}
				isInPageNavigation={true}
				onOptionChange={handleSectionClick}
			/>
		);
	}

	return (
		<nav 
			ref={navRef}
			className={`section-navigation ${isFixed ? 'fixed z-50 left-0 right-0' : ''} ${isHeaderVisible ? 'top-6' : 'top-52'} transition-[top] duration-[1000ms] linear z-30 px-8 backdrop-blur-[15px] mx-auto w-max_content flex justify-center items-center -mt-[1.6rem] ${glassStyleSectionNavigation} ${style}`}
			aria-label="Section navigation"
		>
			<div className="mx-auto">
				<ul className="flex flex-wrap gap-6 justify-center">
					{sections.map((section) => (
						<li key={section.id}>
							<Cta
								displayType={section.displayType}
								label={section.label}
								onClick={() => handleSectionClick(section.contentfulid)}
								style={`!px-2 text-p no-underline transition-colors text-dark_green ${
									currentSection === section.contentfulid
										? 'font-bold'
										: 'font-regular'
									}`}
								href={`#${section.contentfulid}`}
							/>
						</li>
					))}
				</ul>
			</div>
		</nav>
	);
};

export default SectionNavigation;
